import moment from "moment";
import React, { useRef } from "react";
import { useTranslation } from "react-i18next";

import { useListViewWeeks } from "../../../../../hooks/annual-planner";
import useMirrorScrollbar from "../../../../../hooks/useMirrorScrollbar";
import useStickyGridHeader from "../../../../../hooks/useStickyGridHeader";
import {
  getWeeklyStatsAvg,
  resolvePhaseColor,
} from "../../../../../utils/annual-planner-helpers";
import { truncateText } from "../../../../../utils/string-helpers";
import { MilestonePill, PhasePill } from "./Pills";

export const ListView = ({
  dateFromState,
  startingCalendarDay,
  phases,
  datesAttachments,
  nutritionPhases,
  nutritionDatesAttachments,
  phasesDescriptions,
  clientGoals,
  events,
  athleteStatsState,
  athleteVolumesState,
  units,
  onSelectWeek,
  classNames = "",
}) => {
  const { t } = useTranslation("athlete-portal");

  const weeksRows = useListViewWeeks({
    dateFromState,
    startingCalendarDay,
    datesAttachments,
    nutritionDatesAttachments,
    phasesDescriptions,
    clientGoals,
    events,
    athleteStatsState,
    athleteVolumesState,
  });

  const thead = (
    <thead role="table-header">
      <tr>
        <th className="week">{t("annualPlanner.week")}</th>
        <th className="training-phase">{t("annualPlanner.trainingPhase")}</th>
        <th className="nutrition-phase">{t("annualPlanner.nutritionPhase")}</th>
        <th className="milestones">{t("annualPlanner.milestones")}</th>
        <th className="notes">{t("annualPlanner.weeklyNotes")}</th>
        <th className="readiness">{t("annualPlanner.statsReadiness")}</th>
        <th className="session">{t("annualPlanner.statsSessionRpe")}</th>
        <th className="weekly">
          {units === "metric"
            ? t("annualPlanner.statsWeeklyVolumeKg")
            : t("annualPlanner.statsWeeklyVolumeLbs")}
        </th>
        <th className="body">
          {units === "metric"
            ? t("annualPlanner.bodyWeightKg")
            : t("annualPlanner.bodyWeightLbs")}
        </th>
      </tr>
    </thead>
  );

  const tbody = (
    <tbody role="table-body">
      {weeksRows.map(
        (
          {
            weekFirstDay,
            weekTrainingPhase,
            weekNutritionPhase,
            weekMilestones,
            weekComment,
            weekStats,
            weekVolumes,
          },
          index
        ) => (
          <tr key={`week-row-${index}`}>
            <td>{moment(weekFirstDay).format("MMM D, YYYY")}</td>
            <DataRow
              index={index}
              length={weeksRows.length}
              phases={phases}
              weekTrainingPhase={weekTrainingPhase}
              nutritionPhases={nutritionPhases}
              weekNutritionPhase={weekNutritionPhase}
              weekMilestones={weekMilestones}
              weekFirstDay={weekFirstDay}
              weekComment={weekComment}
              weekStats={weekStats}
              weekVolumes={weekVolumes}
              units={units}
              onSelectWeek={onSelectWeek}
            />
          </tr>
        )
      )}
    </tbody>
  );

  // The column set here is fixed at build time (unlike TableView.js's
  // Detailed/template toggle) - nothing in this view changes how many
  // columns render, so there's no re-measure dependency to list.
  const {
    scrollRef: gridScrollRef,
    mirrorRef: mirrorScrollRef,
    needsScroll,
    scrollWidth,
    onScrollRef,
    onScrollMirror,
  } = useMirrorScrollbar();

  const tableRef = useRef(null);
  const {
    sentinelRef,
    stickyHeadRef,
    pinned,
    colWidths,
    headerRect,
    onScrollSync,
  } = useStickyGridHeader({
    scrollRef: gridScrollRef,
    containerRef: tableRef,
    getHeaderCells: (table) =>
      Array.from(table.querySelectorAll("thead > tr > th")),
  });

  const onScrollBody = () => {
    onScrollRef();
    onScrollSync();
  };

  return (
    <section role="table-wrapper" className={classNames}>
      {pinned && (
        <div
          className="list-view-sticky-head-fixed"
          style={{ left: headerRect.left, width: headerRect.width }}
        >
          <div className="list-view-sticky-head-inner" ref={stickyHeadRef}>
            <table className="list-view-sticky-head-table">
              <colgroup>
                {colWidths.map((width, colIndex) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: column order is static, never reordered/filtered
                  <col key={colIndex} style={{ width: `${width}px` }} />
                ))}
              </colgroup>
              {thead}
            </table>
          </div>
        </div>
      )}
      <div ref={sentinelRef} />
      <div
        className="list-view-grid-scroll"
        ref={gridScrollRef}
        onScroll={onScrollBody}
      >
        <table ref={tableRef}>
          {thead}
          {tbody}
        </table>
      </div>
      {needsScroll && (
        <div
          className="list-view-mirror-scroll"
          ref={mirrorScrollRef}
          onScroll={onScrollMirror}
        >
          <div
            className="list-view-mirror-scroll__spacer"
            style={{ width: scrollWidth }}
          />
        </div>
      )}
    </section>
  );
};

const EmptyCell = () => <span className="empty-value">-</span>;

const DataRow = ({
  phases,
  weekTrainingPhase,
  nutritionPhases,
  weekNutritionPhase,
  weekMilestones,
  weekFirstDay,
  weekComment,
  weekStats,
  weekVolumes,
  units,
  onSelectWeek,
  index,
  length,
}) => {
  const { t } = useTranslation("athlete-portal");
  const { volumes, readinessAvg, sessionRPEAvg, bodyWeightAvg } =
    getWeeklyStatsAvg(weekStats, weekVolumes, [], units);
  const lastRowClass = index === length - 1 ? " last" : "";

  // formatVolume (getWeeklyStatsAvg) appends " kg"/" lb" to every value - the
  // column header already carries that unit now (see the "weekly" <th>
  // above), so strip the trailing word instead of showing it twice. Same fix
  // as StatsCard.js's own hideVolumeUnit for Calendar view's Weekly Volume row.
  const displayVolumes = volumes?.replace(/ (kg|lb)$/, "");

  return (
    <React.Fragment>
      <td className={`column training-phase${lastRowClass}`}>
        {weekTrainingPhase ? (
          <PhasePill
            name={weekTrainingPhase.phaseName}
            color={resolvePhaseColor(weekTrainingPhase.phaseName, phases)}
          />
        ) : (
          <EmptyCell />
        )}
      </td>
      <td className={`column nutrition-phase${lastRowClass}`}>
        {weekNutritionPhase ? (
          <PhasePill
            name={weekNutritionPhase.phaseName}
            color={resolvePhaseColor(
              weekNutritionPhase.phaseName,
              nutritionPhases
            )}
          />
        ) : (
          <EmptyCell />
        )}
      </td>
      <td className={`column milestones${lastRowClass}`}>
        {weekMilestones.length ? (
          <div className="milestones-list">
            {weekMilestones.map((milestone, milestoneIndex) => (
              <MilestonePill
                key={`${milestone.kind}-${milestoneIndex}`}
                milestone={milestone}
                units={units}
                t={t}
              />
            ))}
          </div>
        ) : (
          <EmptyCell />
        )}
      </td>
      <td className={`column notes${lastRowClass}`}>
        <button
          type="button"
          className={`week-note${weekComment ? "" : " placeholder"}`}
          onClick={() => onSelectWeek(weekFirstDay)}
        >
          {weekComment
            ? truncateText(weekComment.phaseDescription, 15)
            : t("annualPlanner.addNotes")}
        </button>
      </td>
      <td className="week-readiness">{readinessAvg || ""}</td>
      <td className="week-sessionrpe">{sessionRPEAvg || ""}</td>
      <td className="week-volumes">{displayVolumes || ""}</td>
      <td className="week-bodyweight">{bodyWeightAvg || ""}</td>
    </React.Fragment>
  );
};
